import type { CSSProperties } from 'react';
import './semantic.groupB.css';

/**
 * Foundation semantic: TensionGauge（Phase D §4.4 / Phase B 凍結対象 API）
 *
 * タイマーや緊迫度を表す bar/numeric の 2 形式ゲージ。
 * Visual Grammar は段階固定:
 *   - current ≥ successThreshold → var(--mg-success)
 *   - current ≥ warningThreshold → var(--mg-hint)
 *   - current <  criticalThreshold → var(--mg-error)（+ pulse 演出）
 * しきい値は「残りが多いほど安全」前提（残り時間/残量に整合）。
 * 配色は全て mood トークン経由（hex 直書きはしない）。
 */
export type TensionGaugeVariant = 'bar' | 'numeric';
export type TensionGaugeFormat = 'percent' | 'mmss';

export interface TensionGaugeProps {
  /** 現在値（max との比で段階色と幅を決める） */
  current: number;
  /** 最大値（既定 100）。0 以下が来た場合は 100 にフォールバック */
  max?: number;
  /** 表示形式（既定 'bar'） */
  variant?: TensionGaugeVariant;
  /** numeric 時の表記（既定 'percent'） */
  format?: TensionGaugeFormat;
  /** 任意ラベル（残り時間 等） */
  label?: string;
  /**
   * critical 段階の閾値（current < critical で error 色 + pulse、既定 25）。
   * 単位は % ではなく current と同じスケールではなく「current/max を 0..100 に正規化した値」。
   * 例: max=60 でも criticalThreshold=25 は「残量 25% 未満」を意味する。
   */
  criticalThreshold?: number;
  /** warning 段階の閾値（既定 50） */
  warningThreshold?: number;
  /** success 段階の閾値（既定 75） */
  successThreshold?: number;
  /** 追加 className（呼び出し側で位置調整等するため） */
  className?: string;
}

/**
 * 0..100 のパーセント値から段階ラベルを決める。
 * Visual Grammar の段階対応は CSS 側で data-stage 属性に紐づく。
 */
function resolveStage(
  pct: number,
  critical: number,
  warning: number,
  success: number,
): 'success' | 'warning' | 'critical' | 'neutral' {
  if (pct < critical) return 'critical';
  if (pct < warning) return 'warning';
  if (pct >= success) return 'success';
  // success にも届かないが warning 以上＝中間域。Visual Grammar の主役色は success 系で安全側に倒す。
  return 'success';
}

/** 0..max を 0..100 にクランプして返す（max<=0 は 0 扱い） */
function toPercent(current: number, max: number): number {
  if (!Number.isFinite(current) || !Number.isFinite(max) || max <= 0) return 0;
  const raw = (current / max) * 100;
  if (raw < 0) return 0;
  if (raw > 100) return 100;
  return raw;
}

/** mm:ss 表記。current は「秒」として解釈する（呼び出し側で秒を渡す前提） */
function formatMmSs(secondsInput: number): string {
  const total = Math.max(0, Math.floor(secondsInput));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function TensionGauge({
  current,
  max = 100,
  variant = 'bar',
  format = 'percent',
  label,
  criticalThreshold = 25,
  warningThreshold = 50,
  successThreshold = 75,
  className,
}: TensionGaugeProps) {
  // max<=0 のような不正値でも UI が破綻しないよう 100 にフォールバック
  const safeMax = max > 0 ? max : 100;
  const pct = toPercent(current, safeMax);
  const stage = resolveStage(pct, criticalThreshold, warningThreshold, successThreshold);

  const rootClasses = ['mg-gauge', `mg-gauge--${variant}`, className]
    .filter(Boolean)
    .join(' ');

  // CSS 変数で動的な幅を渡す（hex 直書きにならず、CSS 側のトークンに委ねる）
  const fillStyle: CSSProperties = { ['--mg-gauge-fill' as string]: `${pct}%` };

  // numeric の表示テキスト（percent=整数%、mmss=current を秒として扱う）
  const numericText =
    format === 'mmss' ? formatMmSs(current) : `${Math.round(pct)}%`;

  return (
    <div
      className={rootClasses}
      data-stage={stage}
      role={variant === 'bar' ? 'progressbar' : undefined}
      aria-valuemin={variant === 'bar' ? 0 : undefined}
      aria-valuemax={variant === 'bar' ? safeMax : undefined}
      aria-valuenow={variant === 'bar' ? current : undefined}
      aria-label={label}
    >
      {label ? <span className="mg-gauge__label">{label}</span> : null}
      {variant === 'bar' ? (
        <div className="mg-gauge__track">
          <div className="mg-gauge__fill" style={fillStyle} />
        </div>
      ) : (
        <span className="mg-gauge__value">{numericText}</span>
      )}
    </div>
  );
}
